
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDirection } from '@/providers/DirectionProvider';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/providers/AuthProvider';
import { useNavigate, useLocation } from 'react-router-dom';
import PhoneVerification from './PhoneVerification';
import { REQUIRE_PHONE_VERIFICATION_EVENT } from '@/utils/requirePhoneVerification';
import { REQUEST_FORM_STORAGE_KEY, consumeStoredFormSendIntent } from '@/utils/storedRequestForm';

const PhoneRequiredDialog: React.FC = () => {
  const { t } = useTranslation();
  const { direction } = useDirection();
  const { user, phoneVerified, checkPhoneVerification } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // Id of the user who closed the dialog. It stays closed for them until they try to send something that needs
  // a verified phone (REQUIRE_PHONE_VERIFICATION_EVENT) or reload the page.
  const [dismissedFor, setDismissedFor] = useState<string | null>(null);
  // The /chat landing is a full-screen layer above this dialog (z-60 vs z-50). An open modal behind it would make
  // the whole page unclickable (body pointer-events: none), so the chat sends the visitor to /login for verification.
  const onChat = location.pathname === '/chat';

  useEffect(() => {
    // A lookup that is still running when the visitor closes the dialog (or when anything else here changes) must not
    // act on its answer: it would open the dialog again from a stale closure.
    let cancelled = false;
    const checkPhone = async () => {
      // Only show for logged in users who don't have a phone verified
      if (user && !onChat) {
        // Check database first (source of truth) via checkPhoneVerification
        const hasPhone = await checkPhoneVerification();
        if (cancelled) return;

        if (hasPhone) {
          console.log('Phone verified in database, not showing dialog');
          setOpen(false);
          return;
        }

        // Only open dialog if user doesn't have a phone verified and has not just closed it
        if (dismissedFor !== user.id) setOpen(true);
      } else {
        setOpen(false);
      }
    };

    checkPhone();
    return () => { cancelled = true; };
  }, [user?.id, phoneVerified, dismissedFor, onChat]);

  useEffect(() => {
    const reopen = () => {
      if (!user || onChat) return;
      setDismissedFor(null);
      setOpen(true);
    };
    window.addEventListener(REQUIRE_PHONE_VERIFICATION_EVENT, reopen);
    return () => window.removeEventListener(REQUIRE_PHONE_VERIFICATION_EVENT, reopen);
  }, [user, onChat]);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next && user) setDismissedFor(user.id);
  };

  const handleVerified = () => {
    setOpen(false);
    
    // Visitor came from the /chat landing: back to the chat, which sends the request it built
    if (location.pathname === '/login' && location.state?.returnTo === '/chat') {
      navigate('/chat', { state: { resumePending: true }, replace: true });
      return;
    }

    // Check if there's a pending request form
    const storedFormData = localStorage.getItem(REQUEST_FORM_STORAGE_KEY);
    
    if (storedFormData) {
      console.log('Found pending form data');
      // If already on home page, just close the dialog - the form is already restored
      if (location.pathname === '/') {
        console.log('Already on home page, staying here');
        return;
      }
      // Only a form the visitor just sent here from the home page (marker and form both under 10 minutes old) goes back
      // there and out by itself. An older one is left for restoring and this verification does nothing with it.
      if (consumeStoredFormSendIntent()) {
        navigate('/', { 
          state: { 
            submitPendingRequest: true,
            openRequestForm: true 
          },
          replace: true 
        });
        return;
      }
    }
    
    // If we're on the login page, redirect to dashboard
    if (location.pathname === '/login') {
      navigate('/dashboard');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        dir={direction}
      >
        <DialogHeader>
          <DialogTitle>{t('dialogs.phoneRequired.title')}</DialogTitle>
          <DialogDescription>
            {t('dialogs.phoneRequired.description')}
          </DialogDescription>
        </DialogHeader>

        {user && (
          <PhoneVerification
            phone={user.user_metadata?.phone || ''}
            onVerified={handleVerified}
            isPostLogin={true}
            autoSend={false}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PhoneRequiredDialog;
